/**
 * URL Sanitization Utility
 * Prevents XSS attacks via javascript: and other malicious URL protocols
 */

/**
 * Sanitizes a URL to prevent javascript: XSS attacks
 * Only allows http:// and https:// protocols
 * 
 * @param url - The URL to sanitize
 * @returns The sanitized URL, or null if the URL is invalid/malicious
 */
export function sanitizeUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null;
  
  const trimmed = url.trim();
  if (!trimmed) return null;
  
  const lowerUrl = trimmed.toLowerCase();
  
  // Block dangerous protocols
  const dangerousProtocols = [
    'javascript:',
    'vbscript:',
    'data:',
    'file:',
    'blob:',
  ];
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      console.warn(`[sanitizeUrl] Blocked dangerous URL: ${url.substring(0, 50)}...`);
      return null;
    }
  }
  
  // Allow http and https
  if (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://')) {
    return trimmed;
  }
  
  // If no protocol specified, prepend https://
  if (!lowerUrl.includes('://')) {
    return `https://${trimmed}`;
  }
  
  // Unknown protocol - block it
  console.warn(`[sanitizeUrl] Blocked unknown protocol URL: ${url.substring(0, 50)}...`);
  return null;
}

/**
 * Sanitizes a Twitter/X handle or URL
 * Returns a safe Twitter URL
 */
export function sanitizeTwitterUrl(input: string | null | undefined): string | null {
  if (!input || typeof input !== 'string') return null;
  
  const trimmed = input.trim();
  if (!trimmed) return null;
  
  // If it's already a URL, sanitize it
  if (trimmed.includes('twitter.com') || trimmed.includes('x.com')) {
    return sanitizeUrl(trimmed);
  }
  
  // If it's a handle (with or without @), construct URL
  const handle = trimmed.replace(/^@/, '').replace(/[^a-zA-Z0-9_]/g, '');
  if (handle) {
    return `https://x.com/${handle}`;
  }
  
  return null;
}

/**
 * Sanitizes a Telegram handle or URL
 * Returns a safe Telegram URL
 */
export function sanitizeTelegramUrl(input: string | null | undefined): string | null {
  if (!input || typeof input !== 'string') return null;
  
  const trimmed = input.trim();
  if (!trimmed) return null;
  
  // If it's already a URL, sanitize it
  if (trimmed.includes('t.me') || trimmed.includes('telegram.')) {
    return sanitizeUrl(trimmed);
  }
  
  // If it's a handle (with or without @), construct URL
  const handle = trimmed.replace(/^@/, '').replace(/[^a-zA-Z0-9_]/g, '');
  if (handle) {
    return `https://t.me/${handle}`;
  }
  
  return null;
}

